import type { Milliseconds, Seconds } from '@lemonade-hq/ts-helpers';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { motion } from 'motion/react';
import { useMemo } from 'react';
import * as styles from './ReasoningText.css';

interface ReasoningTextProps {
  readonly text: string;
  readonly delayAnimation?: boolean;
  readonly msPerChar?: Milliseconds;
  readonly spread?: number;
}

const DEFAULT_MS_PER_CHAR: Milliseconds = 50;
const DEFAULT_SPREAD = 30;
const MIN_DURATION: Seconds = 1.5;
const MAX_DURATION: Seconds = 6;

export const ReasoningText = ({
  text,
  delayAnimation,
  msPerChar = DEFAULT_MS_PER_CHAR,
  spread = DEFAULT_SPREAD,
}: ReasoningTextProps): JSX.Element => {
  const animationDelay = delayAnimation ? styles.DELAY_ANIMATION_DURATION : 0;

  const duration = useMemo(() => {
    const baseDuration = (text.length * msPerChar) / 1000;
    return Math.max(MIN_DURATION, Math.min(MAX_DURATION, baseDuration));
  }, [text.length, msPerChar]);

  return (
    <motion.span
      animate={{
        opacity: 1,
        y: 0,
        backgroundPosition: '0% center',
      }}
      className={styles.shimmerGradient}
      exit={{
        opacity: 0,
        y: `-${styles.TRANSLATE_OFFSET}`,
      }}
      initial={{
        opacity: 0,
        y: styles.TRANSLATE_OFFSET,
        backgroundPosition: '100% center',
      }}
      key={text}
      style={assignInlineVars({
        [styles.shimmerSpreadVar]: `${spread}px`,
      })}
      transition={{
        opacity: {
          duration: styles.OPACITY_ANIMATION_DURATION,
          delay: animationDelay,
        },
        y: {
          duration: styles.TRANSLATE_ANIMATION_DURATION,
          delay: animationDelay,
        },
        backgroundPosition: {
          repeat: Infinity,
          duration,
          ease: 'linear',
          delay: animationDelay,
        },
      }}
    >
      {text}
    </motion.span>
  );
};
